// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, ToolCase, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type OctoRule, type OctoRuleStage, type OctoRulesSortBy, useOctoRulesQuery } from "@/api/hooks/use-octo-rules";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { SushiPlusIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterPresets } from "@/components/shared/filter-presets";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CreateOctoRuleDialog } from "@/features/octo/create-octo-rule-dialog";
import { EvaluateOctoRulesDialog } from "@/features/octo/evaluate-octo-rules-dialog";
import { OctoRuleActionsMenu } from "@/features/octo/octo-rule-actions-menu";
import { OctoOutcomeBadge, OctoStageBadge } from "@/features/octo/octo-rule-badges";
import { ToggleOctoRuleActiveDialog } from "@/features/octo/toggle-octo-rule-active-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/octo/rules/index";

type OctoRuleRow = OctoRule & { chainName: string; chainLegibleName: string; storeName: string };

const STAGES: OctoRuleStage[] = ["creation", "visibility", "workability"];

const FILTER_DEFAULTS = {
  search: "",
  onlyActive: true,
  chain: "",
  store: "",
  stage: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "priority",
  sortDesc: true,
};

export function OctoRulesList() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.octoRulesEdit);
  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  const [toggleRule, setToggleRule] = useState<OctoRuleRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [evaluateOpen, setEvaluateOpen] = useState(false);
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);
  const [chainFilter, setChainFilter] = useState(initial.chain);
  const [storeFilter, setStoreFilter] = useState(initial.store);
  const [stageFilter, setStageFilter] = useState(initial.stage);

  const sortBy = (sorting[0]?.id ?? "priority") as OctoRulesSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);
  const isFiltered = !!(search || chainFilter || storeFilter || stageFilter || !onlyActive);

  const clearFilters = useCallback(() => {
    setSearch("");
    setChainFilter("");
    setStoreFilter("");
    setStageFilter("");
    setOnlyActive(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({
    isFiltered,
    clearFilters,
    disabled: createOpen || evaluateOpen || toggleRule !== null,
  });

  // The single description of "what is filtered right now" — shared by the URL sync and the presets.
  const currentFilters = useMemo(
    () => ({
      search,
      onlyActive,
      chain: chainFilter,
      store: storeFilter,
      stage: stageFilter,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id ?? "priority",
      sortDesc: sorting[0]?.desc ?? false,
    }),
    [search, onlyActive, chainFilter, storeFilter, stageFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const syncUrl = useCallback(() => {
    const encoded = encodeFilters(currentFilters, FILTER_DEFAULTS);
    navigate({ to: "/octo/rules", search: encoded ? { f: encoded } : {}, replace: true });
  }, [currentFilters, navigate]);

  const applyPreset = useCallback(function applyPreset(next: typeof FILTER_DEFAULTS) {
    setSearch(next.search);
    setOnlyActive(next.onlyActive);
    setChainFilter(next.chain);
    setStoreFilter(next.store);
    setStageFilter(next.stage);
    setSorting([{ id: next.sortId, desc: next.sortDesc }]);
    // A preset carries no page index, and page size stays the viewer's own preference.
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const { data, isLoading, refetch, isFetching } = useOctoRulesQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyActive: onlyActive || undefined,
    chainName: chainFilter || undefined,
    storeName: storeFilter || undefined,
    stage: (stageFilter || undefined) as OctoRuleStage | undefined,
  });

  const chainMap = useMemo(() => new Map((data?.chains ?? []).map((c) => [c.chainId, c])), [data?.chains]);
  const storeMap = useMemo(() => new Map((data?.stores ?? []).map((s) => [s.storeId, s])), [data?.stores]);

  const rows = useMemo<OctoRuleRow[]>(
    () =>
      (data?.octoRules ?? []).map((rule) => {
        const chain = rule.chainId != null ? chainMap.get(rule.chainId) : undefined;
        const store = rule.storeId != null ? storeMap.get(rule.storeId) : undefined;
        return {
          ...rule,
          chainName: chain?.chainName ?? "",
          chainLegibleName: chain?.legibleName ?? "",
          storeName: store?.storeName ?? "",
        };
      }),
    [data?.octoRules, chainMap, storeMap],
  );

  const columns = useMemo<DataTableColumnDef<OctoRuleRow>[]>(
    () => [
      {
        accessorKey: "octoRuleId",
        header: t("octoRules.list.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("octoRules.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.octoRuleId} />,
      },
      {
        accessorKey: "name",
        header: t("octoRules.list.columns.name"),
        meta: { columnLabel: t("octoRules.list.columns.name") },
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <Link
              to="/octo/rules/$ruleId"
              params={{ ruleId: String(row.original.octoRuleId) }}
              className="font-medium text-primary hover:underline w-fit"
            >
              {row.original.name || t("octoRules.unnamed", { id: row.original.octoRuleId })}
            </Link>
            {row.original.description && (
              <span className="block max-w-sm truncate text-xs text-muted-foreground">{row.original.description}</span>
            )}
          </div>
        ),
      },
      {
        id: "scope",
        header: t("octoRules.list.columns.scope"),
        enableSorting: false,
        meta: { columnLabel: t("octoRules.list.columns.scope") },
        cell: ({ row }) => {
          if (row.original.storeName) {
            const store = row.original.storeId != null ? storeMap.get(row.original.storeId) : undefined;
            return <StoreLink storeName={row.original.storeName} legibleName={store?.legibleName} />;
          }
          if (row.original.chainName) {
            return <ChainLink chainName={row.original.chainName} legibleName={row.original.chainLegibleName} />;
          }
          return <span className="text-muted-foreground">{t("octoRules.scopeGlobal")}</span>;
        },
      },
      {
        accessorKey: "stage",
        header: t("octoRules.list.columns.stage"),
        size: 130,
        meta: { columnLabel: t("octoRules.list.columns.stage") },
        cell: ({ row }) => <OctoStageBadge stage={row.original.stage} />,
      },
      {
        accessorKey: "outcome",
        header: t("octoRules.list.columns.outcome"),
        size: 90,
        meta: { columnLabel: t("octoRules.list.columns.outcome") },
        cell: ({ row }) => <OctoOutcomeBadge outcome={row.original.outcome} />,
      },
      {
        accessorKey: "priority",
        header: t("octoRules.list.columns.priority"),
        size: 90,
        meta: { columnLabel: t("octoRules.list.columns.priority") },
        cell: ({ row }) => <span className="font-mono tabular-nums">{row.original.priority}</span>,
      },
      {
        accessorKey: "createdAt",
        header: t("octoRules.list.columns.createdAt"),
        meta: { columnLabel: t("octoRules.list.columns.createdAt") },
        cell: ({ row }) => <DateValue value={row.original.createdAt} />,
      },
      {
        id: "isActive",
        header: t("octoRules.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("octoRules.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      ...(canEdit
        ? [
            {
              id: "actions",
              size: 50,
              meta: { forceWidth: true, sticky: "right" },
              enableSorting: false,
              enableHiding: false,
              cell: ({ row }) => (
                <OctoRuleActionsMenu
                  isActive={row.original.isActive}
                  onToggleActive={() => setToggleRule(row.original)}
                />
              ),
            } satisfies DataTableColumnDef<OctoRuleRow>,
          ]
        : []),
    ],
    [t, storeMap, canEdit],
  );

  return (
    <div>
      <PageHeader title={t("octoRules.title")} />
      <div className="p-6">
        <DataTable
          storageKey="octo-rules-list"
          columns={columns}
          data={rows}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={refetch}
          isRefreshing={isFetching}
          searchPlaceholder={t("octoRules.list.searchPlaceholder")}
          presets={
            <FilterPresets
              storageKey="octo-rules-list"
              defaults={FILTER_DEFAULTS}
              current={currentFilters}
              onApply={applyPreset}
            />
          }
          filters={
            <>
              <FilterSelect
                label={t("octoRules.list.filter.chain")}
                value={chainFilter}
                onChange={(v) => {
                  setChainFilter(v);
                  setStoreFilter("");
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                searchPlaceholder={t("stores.form.searchChains")}
                emptyText={t("stores.form.noChains")}
                loading={contextLoading}
              />
              <FilterSelect
                label={t("octoRules.list.filter.store")}
                value={storeFilter}
                onChange={(v) => {
                  setStoreFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.stores ?? []).map((s) => ({ label: s.display, value: s.value }))}
                searchPlaceholder={t("users.list.filter.searchStores")}
                emptyText={t("users.list.filter.noStores")}
                loading={contextLoading}
              />
              <FilterSelect
                label={t("octoRules.list.filter.stage")}
                value={stageFilter}
                onChange={(v) => {
                  setStageFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={STAGES.map((s) => ({ label: t(`octoRules.stage.${s}`), value: s }))}
                searchPlaceholder={t("octoRules.list.filter.searchStages")}
                emptyText={t("octoRules.list.filter.noStages")}
              />
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            <>
              {/* Tools are read-only utilities — available to anyone who can view the rules. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label={t("octoRules.tools.menuLabel")}>
                    <ToolCase className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={-36} className="dropdown-overlap">
                  <DropdownMenuLabel className="flex items-center gap-1.5 text-xs font-normal uppercase tracking-wider text-muted-foreground">
                    <ToolCase className="h-3.5 w-3.5" />
                    {t("octoRules.tools.menuLabel")}
                  </DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => setEvaluateOpen(true)}>
                    {t("octoRules.tools.evaluate")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {canEdit && (
                <Button onClick={() => setCreateOpen(true)}>
                  <SushiPlusIcon className="h-4 w-4" />
                  {t("octoRules.list.addRuleButton")}
                </Button>
              )}
            </>
          }
        />
      </div>

      <CreateOctoRuleDialog open={createOpen} onOpenChange={setCreateOpen} />

      <EvaluateOctoRulesDialog open={evaluateOpen} onOpenChange={setEvaluateOpen} />

      <ToggleOctoRuleActiveDialog
        rule={
          toggleRule
            ? {
                octoRuleId: toggleRule.octoRuleId,
                name: toggleRule.name || t("octoRules.unnamed", { id: toggleRule.octoRuleId }),
                isActive: toggleRule.isActive,
              }
            : null
        }
        onClose={() => setToggleRule(null)}
      />
    </div>
  );
}
